import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { HttpError } from '../api/client';
import { API_ERROR_CODES, type EventType, type EventVisibility, type UserDto } from '../api/types';
import { validateEventFormDraft } from '../domain/events/eventForm';
import { hasResourceTimeConflict } from '../domain/events/hasResourceTimeConflict';
import { isoToDatePart, isoToTimeHHMM } from '../domain/events/iso';
import { countParticipants } from '../domain/events/participants';
import { canCreateEvent, canEditEvent } from '../domain/events/policy';
import { clamp, suggestEndTimeHHMM } from '../domain/events/time';
import { formatRuleTime, openResourceIdsOnDate, rulesForResourceOnDate } from '../domain/guestAccess/guestAccess';
import {
  VIP_COMBINED_RESOURCE_ID,
  VIP_SPLIT_RESOURCE_IDS,
  getDefaultResourceId,
  getEventTypeByResourceId,
  getResourceCapacity,
  getResourceMaxParticipants,
  getResourceLabel,
  hasResource,
  isKnownResource,
  normalizeResourceId,
} from '../resources/resources';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import type { RootState } from '../store/store';
import { cancelEvent, createEvent, inviteToEvent, joinAndConfirmEvent, updateEvent } from '../store/thunks';
import { combineDateAndTimeToUtcIso, formatDateYYYYMMDD, parseDateYYYYMMDD } from '../utils/date';
import { compareIsoLocalDateTime } from '../utils/iso';

import { useActiveGuestAccess } from './useActiveGuestAccess';

export type TouchedFields = {
  title: boolean;
  date: boolean;
  startTime: boolean;
  endTime: boolean;
  maxParticipants: boolean;
};

export type UseEventFormResult = {
  // State
  isEdit: boolean;
  title: string;
  description: string;
  resourceId: string;
  selectedDate: Date;
  dateValue: string;
  startTime: string;
  endTime: string;
  maxParticipants: number;
  visibility: EventVisibility;
  type: EventType;
  isSubmitting: boolean;
  touched: TouchedFields;
  invitedUserIds: string[];
  invitedUsers: UserDto[];
  cancelConfirmOpen: boolean;

  // Computed
  minMaxParticipants: number;
  maxParticipantsLimit: number;
  today: Date;
  isValid: boolean;

  // Errors
  titleError: string | null;
  dateError: string | null;
  timeError: string | null;
  maxParticipantsError: string | null;
  resourceNotice: string | null;
  /** Tables open to this guest on the selected date; null for organizers/admins (no restriction). */
  guestOpenResourceIds: string[] | null;
  guestAccessHint: string | null;
  unavailableVipResourceIds: string[];

  gameId: string | null;

  // Actions
  setTitle: (value: string) => void;
  setDescription: (value: string) => void;
  setResourceId: (value: string) => void;
  setSelectedDate: (value: Date) => void;
  updateStartTime: (value: string) => void;
  setEndTime: (value: string) => void;
  setMaxParticipants: React.Dispatch<React.SetStateAction<number>>;
  setVisibility: (value: EventVisibility) => void;
  setTouched: React.Dispatch<React.SetStateAction<TouchedFields>>;
  setInvitedUserIds: React.Dispatch<React.SetStateAction<string[]>>;
  setCancelConfirmOpen: (value: boolean) => void;
  setGameId: (value: string | null) => void;

  handleAddMember: (telegramId: string) => void;
  handleAddGroup: (groupId: string) => void;
  handleRemoveUser: (telegramId: string) => void;
  reservedSlotsCount: number;
  handleAddReservedSlot: () => void;
  handleRemoveReservedSlot: () => void;

  onSubmit: () => Promise<void>;
  onCancelEvent: () => Promise<void>;
  goBack: () => void;
};

export function useEventForm(): UseEventFormResult {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { eventId } = useParams();

  const isEdit = Boolean(eventId);

  const role = useAppSelector((s: RootState) => s.auth.user?.role);
  const currentUserTelegramId = useAppSelector((s: RootState) => s.auth.user?.telegramId);
  const event = useAppSelector((s: RootState) =>
    eventId ? s.entities.events.items.find((e) => e.id === eventId) ?? null : null,
  );

  const { rules: guestRules, loaded: guestRulesLoaded } = useActiveGuestAccess();
  const isGuest = role === 'user';
  const allEvents = useAppSelector((s: RootState) => s.entities.events.items);
  const allUsers = useAppSelector((s: RootState) => s.entities.users.items);
  const allGroups = useAppSelector((s: RootState) => s.entities.groups.items);

  const prefillResourceId = params.get('resourceId') ?? '';
  const prefillDate = params.get('date') ?? '';

  const today = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const normalizedPrefillResourceId = normalizeResourceId(prefillResourceId);
  const prefilledResourceIsKnown = normalizedPrefillResourceId !== '' && isKnownResource(normalizedPrefillResourceId);
  const prefilledResourceIsVisible = prefilledResourceIsKnown && hasResource(normalizedPrefillResourceId);
  const initialResourceId = prefilledResourceIsVisible ? normalizedPrefillResourceId : getDefaultResourceId();
  const initialDate = parseDateYYYYMMDD(prefillDate) ?? today;
  const resourceNotice =
    !isEdit && normalizedPrefillResourceId !== '' && prefilledResourceIsKnown && !prefilledResourceIsVisible
      ? `Ресурс "${getResourceLabel(normalizedPrefillResourceId)}" сейчас скрыт текущей VIP-схемой. Выберите доступный ресурс вручную.`
      : null;

  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [resourceId, setResourceId] = React.useState(initialResourceId);
  const [selectedDate, setSelectedDate] = React.useState<Date>(initialDate);
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [maxParticipants, setMaxParticipants] = React.useState(() => {
    const cap = getResourceCapacity(initialResourceId, 5);
    return clamp(cap, 2, getResourceMaxParticipants(initialResourceId));
  });
  const [visibility, setVisibility] = React.useState<EventVisibility>('open');

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);
  const [invitedUserIds, setInvitedUserIds] = React.useState<string[]>([]);
  const [reservedSlotsCount, setReservedSlotsCount] = React.useState(0);
  const [serverTimeConflictError, setServerTimeConflictError] = React.useState<string | null>(null);

  const [touched, setTouched] = React.useState<TouchedFields>({
    title: false,
    date: false,
    startTime: false,
    endTime: false,
    maxParticipants: false,
  });

  const [isEndTimeManuallySet, setIsEndTimeManuallySet] = React.useState(false);
  const [prefilledFromEvent, setPrefilledFromEvent] = React.useState(false);
  const [gameId, setGameId] = React.useState<string | null>(null);

  const type: EventType = React.useMemo(() => getEventTypeByResourceId(resourceId), [resourceId]);

  const currentParticipantsCount = React.useMemo(() => {
    if (!event) return 0;
    return countParticipants(event);
  }, [event]);

  const minMaxParticipants = Math.max(2, currentParticipantsCount);
  const maxParticipantsLimit = getResourceMaxParticipants(resourceId);

  // Permission check effect
  React.useEffect(() => {
    if (!role) return;

    if (!isEdit) {
      if (isGuest && !guestRulesLoaded) return;
      if (!canCreateEvent(role, guestRules.length > 0)) {
        navigate(-1);
      }
      return;
    }

    if (!eventId) return;
    if (!event) return;
    if (!canEditEvent({ role, event, currentUserTelegramId })) {
      navigate(-1);
    }
  }, [currentUserTelegramId, event, eventId, guestRules.length, guestRulesLoaded, isEdit, isGuest, navigate, role]);

  // Prefill from event effect
  React.useEffect(() => {
    if (!isEdit) return;
    if (!event) return;
    if (prefilledFromEvent) return;

    const datePart = isoToDatePart(event.startAt);
    const startPart = isoToTimeHHMM(event.startAt);
    const endPart = isoToTimeHHMM(event.endAt);

    setTitle(event.title);
    setDescription(event.description ?? '');
    setResourceId(normalizeResourceId(event.resourceId));

    const d = datePart ? parseDateYYYYMMDD(datePart) : null;
    if (d) setSelectedDate(d);
    if (startPart) setStartTime(startPart);
    if (endPart) setEndTime(endPart);
    setVisibility(event.visibility);
    setMaxParticipants(
      clamp(event.maxParticipants, minMaxParticipants, getResourceMaxParticipants(event.resourceId)),
    );
    setGameId(event.game?.id ?? null);
    setIsEndTimeManuallySet(true);
    setPrefilledFromEvent(true);
  }, [event, isEdit, minMaxParticipants, prefilledFromEvent]);

  // Max participants adjustment effect
  React.useEffect(() => {
    if (isEdit) {
      setMaxParticipants((current) => clamp(current, minMaxParticipants, maxParticipantsLimit));
      return;
    }

    const cap = getResourceCapacity(resourceId, 5);
    setMaxParticipants((current) => {
      const next = clamp(current, 2, maxParticipantsLimit);
      const recommended = clamp(cap, 2, maxParticipantsLimit);
      return current === next ? current : recommended;
    });
  }, [isEdit, minMaxParticipants, maxParticipantsLimit, resourceId]);

  const dateValue = formatDateYYYYMMDD(selectedDate);

  const guestOpenResourceIds = React.useMemo(
    () => (isGuest && !isEdit ? Array.from(openResourceIdsOnDate(guestRules, dateValue)) : null),
    [dateValue, guestRules, isEdit, isGuest],
  );

  // A guest can only book tables an admin has opened; keep the selection on one of them.
  React.useEffect(() => {
    if (!guestOpenResourceIds || isEdit) return;
    if (guestOpenResourceIds.length === 0 || guestOpenResourceIds.includes(resourceId)) return;
    setResourceId(guestOpenResourceIds[0]);
  }, [guestOpenResourceIds, isEdit, resourceId]);

  const guestAccessHint = React.useMemo(() => {
    if (!isGuest || isEdit) return null;
    const windows = rulesForResourceOnDate(guestRules, resourceId, dateValue).map(formatRuleTime);
    if (windows.length > 0) return `Гостевой доступ на этом столе: ${windows.join(', ')}. Мероприятие должно целиком уместиться в это время.`;
    return 'На выбранную дату нет открытых для гостей столов. Выберите другую дату.';
  }, [dateValue, guestRules, isEdit, isGuest, resourceId]);

  const unavailableVipResourceIds = React.useMemo(() => {
    const vipSplitIds = Array.from(VIP_SPLIT_RESOURCE_IDS);
    const hasVipSplitEvent = allEvents.some((e) => {
      if (e.status !== 'active') return false;
      if (isEdit && e.id === eventId) return false;
      const eventDay = isoToDatePart(e.startAt);
      if (eventDay !== dateValue) return false;
      const normalized = normalizeResourceId(e.resourceId);
      return VIP_SPLIT_RESOURCE_IDS.has(normalized);
    });

    const hasVipCombinedEvent = allEvents.some((e) => {
      if (e.status !== 'active') return false;
      if (isEdit && e.id === eventId) return false;
      const eventDay = isoToDatePart(e.startAt);
      if (eventDay !== dateValue) return false;
      const normalized = normalizeResourceId(e.resourceId);
      return normalized === VIP_COMBINED_RESOURCE_ID;
    });

    const blocked = new Set<string>();
    if (hasVipSplitEvent) blocked.add(VIP_COMBINED_RESOURCE_ID);
    if (hasVipCombinedEvent) vipSplitIds.forEach((id) => blocked.add(id));
    return Array.from(blocked);
  }, [allEvents, dateValue, eventId, isEdit]);

  const draft = React.useMemo(
    () => ({
      title,
      resourceId,
      selectedDate,
      startTime,
      endTime,
      maxParticipants,
      visibility,
    }),
    [endTime, maxParticipants, resourceId, selectedDate, startTime, title, visibility],
  );

  const validation = React.useMemo(
    () => validateEventFormDraft(draft, { today, minMaxParticipants, maxParticipantsLimit }),
    [draft, minMaxParticipants, maxParticipantsLimit, today],
  );

  // Clear server error when time or resource changes
  React.useEffect(() => {
    setServerTimeConflictError(null);
  }, [startTime, endTime, selectedDate, resourceId]);

  const timeConflictError = React.useMemo(() => {
    if (!validation.isValid) return null;

    const startAt = combineDateAndTimeToUtcIso(selectedDate, startTime);
    const endAt = combineDateAndTimeToUtcIso(selectedDate, endTime);
    if (!startAt || !endAt) return null;
    if (compareIsoLocalDateTime(startAt, endAt) >= 0) return null;

    const isOverlapping = hasResourceTimeConflict({
      events: allEvents,
      resourceId,
      startAt,
      endAt,
      excludeEventId: isEdit ? eventId : undefined,
    });

    return isOverlapping ? 'На это время уже запланировано другое мероприятие для выбранного ресурса или VIP-зоны' : null;
  }, [allEvents, endTime, eventId, isEdit, resourceId, selectedDate, startTime, validation.isValid]);

  const titleError = touched.title ? validation.errors.title : null;
  const dateError = touched.date ? validation.errors.date : null;
  const timeError =
    touched.startTime || touched.endTime
      ? (validation.errors.time ?? timeConflictError ?? serverTimeConflictError)
      : serverTimeConflictError;
  const maxParticipantsError = touched.maxParticipants ? validation.errors.maxParticipants : null;

  const isValid = validation.isValid && parseDateYYYYMMDD(dateValue) != null && !timeConflictError;

  const updateStartTime = React.useCallback((value: string) => {
    setStartTime(value);

    if (!isEndTimeManuallySet) {
      const suggested = suggestEndTimeHHMM(value, 120);
      if (suggested) setEndTime(suggested);
    }
  }, [isEndTimeManuallySet]);

  const handleSetEndTime = React.useCallback((value: string) => {
    setIsEndTimeManuallySet(true);
    setEndTime(value);
  }, []);

  const invitedUsers = React.useMemo<UserDto[]>(() => {
    return allUsers.filter((u) => invitedUserIds.includes(u.telegramId));
  }, [allUsers, invitedUserIds]);

  const handleAddMember = React.useCallback((telegramId: string) => {
    setInvitedUserIds((prev) => {
      if (prev.includes(telegramId)) return prev;
      return [...prev, telegramId];
    });
  }, []);

  const handleAddGroup = React.useCallback((groupId: string) => {
    const group = allGroups.find((g) => g.id === groupId);
    if (!group) return;

    const groupMemberIds = group.members.map((m) => m.telegramId);
    setInvitedUserIds((prev) => {
      const newIds = groupMemberIds.filter((id) => !prev.includes(id));
      if (newIds.length === 0) return prev;
      return [...prev, ...newIds];
    });
  }, [allGroups]);

  const handleRemoveUser = React.useCallback((telegramId: string) => {
    setInvitedUserIds((prev) => prev.filter((id) => id !== telegramId));
  }, []);

  const handleAddReservedSlot = React.useCallback(() => {
    // For create: total = 1 (creator) + invitedUserIds + reservedSlotsCount + 1 must not exceed maxParticipants
    // For edit: currentParticipantsCount + reservedSlotsCount + 1 must not exceed maxParticipants
    setReservedSlotsCount((prev) => {
      const takenSlots = isEdit
        ? currentParticipantsCount + prev
        : 1 + invitedUserIds.length + prev;
      if (takenSlots < maxParticipants) return prev + 1;
      return prev;
    });
  }, [isEdit, currentParticipantsCount, invitedUserIds.length, maxParticipants]);

  const handleRemoveReservedSlot = React.useCallback(() => {
    setReservedSlotsCount((prev) => Math.max(0, prev - 1));
  }, []);

  const onSubmit = React.useCallback(async () => {
    setTouched({
      title: true,
      date: true,
      startTime: true,
      endTime: true,
      maxParticipants: true,
    });
    setServerTimeConflictError(null);

    if (!isValid || isSubmitting) return;

    const startAt = combineDateAndTimeToUtcIso(selectedDate, startTime);
    const endAt = combineDateAndTimeToUtcIso(selectedDate, endTime);
    if (!startAt || !endAt) return;

    setIsSubmitting(true);
    try {
      if (isEdit) {
        if (!eventId) return;
        await dispatch(
          updateEvent({
            eventId,
            payload: {
              title: validation.normalizedTitle,
              type,
              resourceId,
              startAt,
              endAt,
              maxParticipants,
              visibility,
              // Backend: "" clears the stored description, null/omitted keeps it.
              description: description.trim(),
            },
          }),
        ).unwrap();

        if (invitedUserIds.length > 0) {
          await dispatch(
            inviteToEvent({
              eventId,
              telegramIds: invitedUserIds.map(Number),
            }),
          ).unwrap();
        }

        for (let i = 0; i < reservedSlotsCount; i++) {
          await dispatch(joinAndConfirmEvent({ eventId })).unwrap();
        }
      } else {
        await dispatch(
          createEvent({
            title: validation.normalizedTitle,
            type,
            resourceId,
            startAt,
            endAt,
            maxParticipants,
            visibility,
            ...(description.trim() ? { description: description.trim() } : {}),
            ...(gameId && type === 'boardgames' ? { gameId } : {}),
            participants: [
              ...invitedUserIds.map(id => ({ telegramId: id })),
              ...Array.from({ length: reservedSlotsCount }, () => ({
                telegramId: currentUserTelegramId!,
                status: 'confirmed' as const,
              })),
            ],
          }),
        ).unwrap();
      }

      navigate(-1);
    } catch (error) {
      if (error instanceof HttpError && error.payload?.error === API_ERROR_CODES.TIME_CONFLICT) {
        setServerTimeConflictError('На это время уже запланировано другое мероприятие');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [description, dispatch, endTime, eventId, gameId, invitedUserIds, isEdit, isSubmitting, isValid, maxParticipants, navigate, reservedSlotsCount, resourceId, selectedDate, startTime, type, currentUserTelegramId, validation.normalizedTitle, visibility]);

  const onCancelEvent = React.useCallback(async () => {
    if (!eventId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await dispatch(cancelEvent({ eventId })).unwrap();
      navigate(-1);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, eventId, isSubmitting, navigate]);

  const goBack = React.useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return {
    // State
    isEdit,
    title,
    description,
    resourceId,
    selectedDate,
    dateValue,
    startTime,
    endTime,
    maxParticipants,
    visibility,
    type,
    isSubmitting,
    touched,
    invitedUserIds,
    invitedUsers,
    cancelConfirmOpen,
    gameId,

    // Computed
    minMaxParticipants,
    maxParticipantsLimit,
    today,
    isValid,

    // Errors
    titleError,
    dateError,
    timeError,
    maxParticipantsError,
    resourceNotice,
    guestOpenResourceIds,
    guestAccessHint,
    unavailableVipResourceIds,

    // Actions
    setTitle,
    setDescription,
    setResourceId,
    setSelectedDate,
    updateStartTime,
    setEndTime: handleSetEndTime,
    setMaxParticipants,
    setVisibility,
    setTouched,
    setInvitedUserIds,
    setCancelConfirmOpen,
    setGameId,

    handleAddMember,
    handleAddGroup,
    handleRemoveUser,
    reservedSlotsCount,
    handleAddReservedSlot,
    handleRemoveReservedSlot,

    onSubmit,
    onCancelEvent,
    goBack,
  };
}
